// /playback/multi — 多機同步回放（共用時鐘 scrubber，APP#43）。
//
// 選 N 台攝影機 → 單一 time-absolute 共用時鐘驅動全部：拖曳/播放/暫停一次 seek 所有機。
// 每台各自把共用時鐘解析成「該時刻的 clip + 位移」或「該時段無錄影(空缺)」。時鐘是唯一
// 真相來源(advanceClock 依 wall-time × 倍速前進)，各 <video> 為 slave、drift 超限才 re-seek，
// 所以某台緩衝/卡頓不會拖慢整組時間軸。純同步演算法在 ./multicam(有單元測試)；本檔只做
// React↔<video> 接線。段載入(loadFirst/loadMoreRecordings 懶載入)、時間軸數學(./timeline)、scrubber
// (TimelineStrip) 全部沿用單機回放的既有核心。
//
// ls-loop MV.1（帳號 VMS，docs/mycam/mv-spec.md ①）：每格改用 CellPlayer（hls.js）——雲端片段自組 VOD（同網域，修掉
// TS 不能直接播 / mixed content）、SD 與手機本機走回放隧道；每台一條合併時間軸（雲端 → 本機，gap / offline / unknown /
// expired 分開標，lib/vms/timeline）；對齊改成偏差 > 300 ms 調速、> 1.5 s seek（lib/vms/syncClock alignDecision）。
//
// authz：各機沿用既有 playback 權限——能看單機回放才拉得到該機錄影；某機 403/422 → 該格
// 顯示「無法回放」並排除於同步，其餘機不受影響(不阻斷整頁)。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { useDeviceList } from '../devices/useDeviceList';
import { cameraClass, deviceName, type DeviceSummary } from '../devices/deviceModel';
import { dateFromUsSafe, loadFirstRecordings, loadMoreRecordings, toDateInputValue, type DayRecordingClip } from './playbackLib';
import { TimelineStrip } from './TimelineStrip';
import { type TimeRange } from './timeline';
import { advanceClock } from './multicam';
import { LayoutBar } from '../vms/LayoutBar';
import '../vms/vms.css';
import { camZone, dayWindowInZone, sharedZone } from '../../lib/camTz';
import { queryDayRanges } from '../../lib/recPlayback/sdcardQuery';
import { CellPlayer } from '../../lib/vms/cellPlayer';
import { alignDecision } from '../../lib/vms/syncClock';
import { allNotRecorded, buildTimeline, firstRecordedStart, nextRecordedStart, spanAt, type Range, type Span } from '../../lib/vms/timeline';
import './playback.css';

const MAX_CAMS = 4; // 2×2；多路同步解碼負擔，v1 上限 4（對齊 LiveGrid 最小版面）
const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2, 4] as const;
const CLOCK_TICK_MS = 100; // 共用時鐘更新頻率（10Hz：scrubber 夠平滑、成本低）
const timeOnly = (us: number, timeZone?: string) => formatTimestampUs(us, { year: undefined, month: undefined, day: undefined, timeZone });
const LOAD_BEHIND_US = 10 * 60_000_000; // 每次載入：時鐘前 10 分鐘（往回拖幾分鐘不用重載）…
const LOAD_AHEAD_US = 15 * 60_000_000; // …到後 15 分鐘（同一個來源的區段內）
const RETRY_AFTER_MS = 10_000; // 載入失敗後 10 s 才重試同一段
const SEEK_SNAP_MAX_US = 60_000_000; // ?t= 那刻沒錄影時最多往後吸 60 s

type CamGate = 'forbidden' | 'subscription' | 'error';
interface CamLoad {
  status: 'loading' | 'ready' | 'gated';
  clips: DayRecordingClip[];
  /** MV.1：SD / 手機本機的錄影區段（day-ranges）與查詢狀態 */
  local?: Range[];
  localState?: 'ok' | 'pending' | 'unreachable' | 'none';
  gate?: CamGate;
  /** spec 17 §2.2: this camera's oldest range is loaded → stop pulling older on scrub. */
  reachedStart?: boolean;
}

// ---- one camera cell: a CellPlayer slaved to the shared clock ----
interface CamCellProps {
  device: DeviceSummary;
  spans: Span[];
  playheadUs: number | null;
  playing: boolean;
  rate: number;
}

function CamCell({ device, spans, playheadUs, playing, rate }: CamCellProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<CellPlayer | null>(null);
  const loadingRef = useRef(false);
  const failRef = useRef<{ key: string; at: number } | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');
  const zone = camZone(device.device_id);
  const live = useRef({ playheadUs, playing, rate });
  live.current = { playheadUs, playing, rate };

  useEffect(() => {
    if (!videoRef.current) return;
    const p = new CellPlayer(videoRef.current, device.device_id);
    playerRef.current = p;
    return () => {
      p.destroy();
      playerRef.current = null;
    };
  }, [device.device_id]);

  const span = playheadUs === null ? undefined : spanAt(spans, playheadUs);
  const spanKey = span ? `${span.kind}:${span.source ?? ''}:${span.fromUs}` : '';

  // 時鐘進到「有錄影」的區段、而且還沒載入（或換了來源 / 超出載入範圍）→ 載入這一段
  useEffect(() => {
    const p = playerRef.current;
    if (!p || playheadUs === null || !span || span.kind !== 'recorded' || !span.source) return;
    const L = p.loaded;
    if (L && L.source === span.source && playheadUs >= L.fromUs && playheadUs < L.toUs) return;
    if (loadingRef.current) return;
    if (failRef.current && failRef.current.key === spanKey && Date.now() - failRef.current.at < RETRY_AFTER_MS) return;
    const from = Math.max(span.fromUs, playheadUs - LOAD_BEHIND_US);
    const to = Math.min(span.toUs, playheadUs + LOAD_AHEAD_US);
    loadingRef.current = true;
    setState('loading');
    void p.load(span.source, from, to).then((ok) => {
      loadingRef.current = false;
      if (!ok) {
        failRef.current = { key: spanKey, at: Date.now() };
        setState('failed');
        return;
      }
      failRef.current = null;
      const cur = live.current;
      if (cur.playheadUs !== null) p.seekWall(cur.playheadUs);
      p.setRate(cur.rate);
      if (cur.playing) p.play();
      else p.pause();
      setState('ready');
    });
  }, [playheadUs, spanKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 對齊：時鐘才是真相；這格偏差 > 300 ms 調速追、> 1.5 s 直接跳（暫停 / 拖動時 > 150 ms 就跳）
  useEffect(() => {
    const p = playerRef.current;
    if (!p || state !== 'ready' || playheadUs === null || span?.kind !== 'recorded') return;
    const w = p.wallUs();
    if (w === null) return;
    // 給量測用（e2e 讀偏差）：這格現在播到的真實時間；寫 dataset 不觸發重繪
    if (videoRef.current) {
      videoRef.current.dataset.wallUs = String(Math.round(w));
      videoRef.current.dataset.clockUs = String(playheadUs);
    }
    if (!playing) {
      if (Math.abs(w - playheadUs) > 150_000) p.seekWall(playheadUs);
      return;
    }
    const a = alignDecision(w, playheadUs, rate);
    if (a.kind === 'seek') {
      p.seekWall(a.toUs);
      p.setRate(rate);
    } else if (a.kind === 'rate') p.setRate(a.rate);
    else p.setRate(rate);
  }, [playheadUs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const p = playerRef.current;
    if (!p || state !== 'ready') return;
    if (playing && span?.kind === 'recorded') p.play();
    else p.pause();
  }, [playing, state, span?.kind]);

  const showVideo = span?.kind === 'recorded' && state === 'ready';
  const overlay =
    !span || playheadUs === null ? t('multiPlayback.cellLoading')
      : span.kind === 'gap' ? t('multiPlayback.cellNoRecording')
      : span.kind === 'offline' ? t('multiPlayback.cellOffline')
      : span.kind === 'unknown' ? t('multiPlayback.cellUnknown')
      : span.kind === 'expired' ? t('multiPlayback.cellExpired')
      : state === 'failed' ? t('multiPlayback.cellUnplayable')
      : t('multiPlayback.cellLoading');
  const src = span?.kind === 'recorded' ? (span.source === 'cloud' ? t('multiPlayback.sourceCloud') : t('multiPlayback.sourceLocal')) : '';
  const label = `${deviceName(device)}${playheadUs !== null ? ` · ${timeOnly(playheadUs, zone)}` : ''}${src ? ` · ${src}` : ''}`;

  return (
    <div className="pb-mc-cell" data-testid={`mc-cell-${device.device_id}`} data-span-kind={span?.kind ?? ''} data-span-source={span?.source ?? ''}>
      <video ref={videoRef} className="pb-mc-video" playsInline muted style={{ display: showVideo ? undefined : 'none' }} data-testid={`mc-video-${device.device_id}`} />
      {!showVideo && (
        <div className="pb-mc-video pb-mc-video-empty text-secondary" data-testid={`mc-empty-${device.device_id}`}>
          {overlay}
        </div>
      )}
      <span className="pb-mc-cell-label">{label}</span>
    </div>
  );
}

// ---- per-camera coverage lane (per-gap detail on the shared window) ----
function CoverageLane({ device, spans, range }: { device: DeviceSummary; spans: Span[]; range: TimeRange }) {
  const len = Math.max(1, range.toUs - range.fromUs);
  return (
    <div className="pb-mc-lane" data-testid={`mc-lane-${device.device_id}`}>
      <span className="pb-mc-lane-name text-secondary">{deviceName(device)}</span>
      <div className="pb-mc-lane-track" aria-hidden>
        {spans
          .filter((s) => s.kind !== 'gap')
          .map((s) => (
            <span
              key={`${s.kind}-${s.fromUs}`}
              className={`pb-mc-lane-seg pb-mc-lane-seg--${s.kind}${s.source ? ` pb-mc-lane-seg--${s.source}` : ''}`}
              style={{ left: `${((s.fromUs - range.fromUs) / len) * 100}%`, width: `${Math.max(0.3, ((s.toUs - s.fromUs) / len) * 100)}%` }}
            />
          ))}
      </div>
    </div>
  );
}

export default function MultiPlaybackPage() {
  const { t } = useTranslation();
  const { devices } = useDeviceList();
  const [searchParams] = useSearchParams();
  // 從事件中心點事件進來 → 標題旁給「‹ 事件中心」回上一頁（篩選留在 sessionStorage，回去還在）
  const navigate = useNavigate();
  const fromEventCenter = (useLocation().state as { from?: string } | null)?.from === 'eventCenter';

  const cameras = useMemo(
    () => (devices ?? []).filter((d) => cameraClass(d) === 'camera'), // MV.1：離線的也列（雲端錄影照看；本機那格標離線）
    [devices]
  );

  // Preselect from ?ids= (e.g. arriving from the single-cam page), capped at MAX_CAMS.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || cameras.length === 0) return;
    seededRef.current = true;
    const raw = (searchParams.get('ids') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    const valid = raw.filter((id) => cameras.some((c) => c.device_id === id)).slice(0, MAX_CAMS);
    if (valid.length > 0) setSelectedIds(valid);
  }, [cameras, searchParams]);

  const seekParam = useMemo(() => {
    const n = Number(searchParams.get('t'));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);
  const [date, setDate] = useState(() => toDateInputValue(seekParam ? dateFromUsSafe(seekParam) : new Date()));
  const selZone = sharedZone(selectedIds);
  const win = useMemo(() => dayWindowInZone(date, selZone), [date, selZone]);

  const [camData, setCamData] = useState<Record<string, CamLoad>>({});
  const [playheadUs, setPlayheadUs] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((m: string) => {
    setToast(m);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const selectedKey = selectedIds.join(',');

  // Load each selected camera's day of recordings (concurrently). Reset transport so a new
  // selection/date starts clean; the init effect below re-anchors the playhead.
  useEffect(() => {
    let alive = true;
    setPlaying(false);
    setPlayheadUs(null);
    if (selectedIds.length === 0) {
      setCamData({});
      return;
    }
    setCamData(() => {
      const next: Record<string, CamLoad> = {};
      for (const id of selectedIds) next[id] = { status: 'loading', clips: [] };
      return next;
    });
    // spec 17 §2.2 lazy model: each camera loads only its first (newest) range on entry;
    // older ranges are pulled on demand when the shared scrubber crosses a camera's boundary.
    for (const id of selectedIds) {
      // MV.1：本機（SD / 手機本機）區段另外查，慢或查不到都不擋雲端
      setCamData((p) => (p[id] ? { ...p, [id]: { ...p[id], localState: 'pending' } } : p));
      void queryDayRanges(id, win.fromUs, win.toUs)
        .then((r) => {
          if (!alive) return;
          const local = (r.items ?? []).map((x) => ({ fromUs: x.start_us, toUs: x.end_us }));
          const localState = r.state === 'UNREACHABLE' ? 'unreachable' : 'ok';
          setCamData((p) => (p[id] ? { ...p, [id]: { ...p[id], local, localState } } : p));
        })
        .catch(() => {
          if (alive) setCamData((p) => (p[id] ? { ...p, [id]: { ...p[id], localState: 'unreachable' } } : p));
        });
      loadFirstRecordings(id, win)
        .then((res) => {
          if (alive) setCamData((p) => ({ ...p, [id]: { ...p[id], status: 'ready', clips: res.clips, reachedStart: res.reachedStart } }));
        })
        .catch((e: unknown) => {
          if (!alive) return;
          // 回放不受訂閱閘(spec 17 §3.3;後端 media.go 只驗裝置關係)——422 不再視為訂閱失效,落一般 error。403=撤權。
          // MV.1：雲端拿不到時本機照樣能播 → 不是 403 就當 ready（雲端片段空）
          if (e instanceof ApiError && e.status === 403) {
            setCamData((p) => ({ ...p, [id]: { status: 'gated', clips: [], gate: 'forbidden' } }));
            return;
          }
          setCamData((p) => ({ ...p, [id]: { ...p[id], status: 'ready', clips: [] } }));
        });
    }
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, win.fromUs, win.toUs]);

  // Ready cameras (in selection order) + their clip lists for the shared clock.
  const readyCams = useMemo(
    () =>
      selectedIds
        .map((id) => ({ device: cameras.find((c) => c.device_id === id), load: camData[id] }))
        .filter((x): x is { device: DeviceSummary; load: CamLoad } => !!x.device && x.load?.status === 'ready'),
    [selectedIds, cameras, camData]
  );
  // MV.1：每台一條合併時間軸（雲端 → 本機；缺口分種類）
  const perCamSpans = useMemo(
    () =>
      readyCams.map((c) =>
        buildTimeline({
          window: win,
          cloud: c.load.clips.map((x) => ({ fromUs: x.clip_start_ts, toUs: x.clip_end_ts })),
          local: c.load.local ?? [],
          localState: c.load.localState ?? 'pending',
        })
      ),
    [readyCams, win]
  );
  const perCamKey = readyCams.map((c) => `${c.device.device_id}:${c.load.clips.length}:${c.load.local?.length ?? -1}`).join('|');

  // Anchor the playhead once a day's cameras are ready: deep-link ?t= if inside the window,
  // else the earliest footage across cameras (so there's an immediate frame).
  useEffect(() => {
    if (playheadUs !== null || perCamSpans.length === 0) return;
    // ?t= 在當天範圍內 → 就停在那裡（即使那一刻沒錄影：格子要顯示「沒錄影 / 離線」，不是一直載入中；MV.2 事件中心跳過來）
    if (seekParam !== null && seekParam >= win.fromUs && seekParam < win.toUs) {
      setPlayheadUs(seekParam);
      seekSnapRef.current = seekParam;
      return;
    }
    const earliest = firstRecordedStart(perCamSpans);
    if (earliest === null) return;
    setPlayheadUs(earliest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perCamKey, playheadUs]);

  // ?t= 那一刻每台都沒錄影、但 60 s 內有錄影開始（例如事件前 5 s，事件錄影卻從事件當下才開始）→
  // 等每台本機查詢都回來後往前吸到那一段開頭並提示（不靜默跳）；使用者動過時間軸就不吸。
  const seekSnapRef = useRef<number | null>(null);
  useEffect(() => {
    const at = seekSnapRef.current;
    if (at === null || playheadUs !== at || playing) return;
    if (readyCams.some((c) => (c.load.localState ?? 'pending') === 'pending')) return;
    seekSnapRef.current = null;
    if (!allNotRecorded(perCamSpans, at)) return;
    const nxt = nextRecordedStart(perCamSpans, at);
    if (nxt !== null && nxt - at <= SEEK_SNAP_MAX_US) {
      setPlayheadUs(nxt);
      showToast(t('multiPlayback.toastSnapped', { sec: Math.max(1, Math.round((nxt - at) / 1e6)) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perCamKey, playheadUs, playing]);

  // Refs the interval reads (avoid re-arming the clock on every playhead tick).
  const playheadRef = useRef(playheadUs);
  playheadRef.current = playheadUs;
  const rateRef = useRef(rate);
  rateRef.current = rate;
  const perCamSpansRef = useRef(perCamSpans);
  perCamSpansRef.current = perCamSpans;

  // Master clock: while playing, advance the shared time by wall-elapsed × rate. If the
  // clock enters a gap for EVERY camera (dead air) jump to the next footage; if the day
  // window ends, stop. This is the single source of truth all cells slave to.
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const iv = setInterval(() => {
      const now = performance.now();
      const elapsed = now - last;
      last = now;
      const cur = playheadRef.current;
      if (cur === null) return;
      if (allNotRecorded(perCamSpansRef.current, cur)) {
        const nxt = nextRecordedStart(perCamSpansRef.current, cur);
        if (nxt === null) {
          setPlaying(false);
          showToast(t('multiPlayback.toastReachedEnd'));
          return;
        }
        setPlayheadUs(nxt);
        return;
      }
      const step = advanceClock(cur, elapsed, rateRef.current, win);
      setPlayheadUs(step.us);
      if (step.ended) {
        setPlaying(false);
        showToast(t('multiPlayback.toastReachedEnd'));
      }
    }, CLOCK_TICK_MS);
    return () => clearInterval(iv);
  }, [playing, win, showToast, t]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= MAX_CAMS ? cur : [...cur, id]));
  }, []);

  // Ref so the scrubber handler reads current per-cam load state without re-arming on every tick.
  const camDataRef = useRef(camData);
  camDataRef.current = camData;
  const onSeek = useCallback(
    (us: number) => {
      setPlayheadUs(us);
      // Scrubber-boundary trigger (spec 17 §2.2): if the shared clock crosses before a camera's
      // oldest-loaded clip and older ranges remain, pull that camera's next earlier range.
      for (const id of selectedIds) {
        const cam = camDataRef.current[id];
        if (!cam || cam.status !== 'ready' || cam.reachedStart) continue;
        const oldest = cam.clips[0];
        if (oldest && us < oldest.clip_start_ts) {
          void loadMoreRecordings(id, win)
            .then((res) => {
              setCamData((p) => (p[id] ? { ...p, [id]: { ...p[id], clips: res.clips, reachedStart: res.reachedStart } } : p));
            })
            .catch(() => {
              /* transient — retriggers on the next scrub */
            });
        }
      }
    },
    [selectedIds, win],
  );
  const togglePlay = useCallback(() => {
    if (perCamSpansRef.current.length === 0) return;
    setPlaying((p) => !p);
  }, []);

  const anyReady = readyCams.length > 0;
  const anyLoading = selectedIds.some((id) => camData[id]?.status === 'loading');
  const gatedCams = selectedIds
    .map((id) => ({ device: cameras.find((c) => c.device_id === id), load: camData[id] }))
    .filter((x): x is { device: DeviceSummary; load: CamLoad } => !!x.device && x.load?.status === 'gated');
  const cols = Math.max(1, Math.ceil(Math.sqrt(readyCams.length)));
  // TimelineStrip coalesces the clips it is given → pass every camera's clips so the shared
  // scrubber's segments render the UNION coverage (any camera recording ⇒ footage there).
  // MV.1：union 覆蓋改用合併時間軸的「有錄影」區段（雲端 + 本機）
  const unionRaw = useMemo(
    () =>
      perCamSpans.flat().filter((s) => s.kind === 'recorded').map((s) => ({ clip_id: '', clip_start_ts: s.fromUs, clip_end_ts: s.toUs } as DayRecordingClip)),
    [perCamSpans]
  );

  return (
    <section className="pb-page pb-mc-page">
      <header className="pb-header">
        {fromEventCenter && (
          <button type="button" className="btn-ghost" onClick={() => navigate(-1)} data-testid="mc-back-event-center">
            ‹ {t('eventCenter.title')}
          </button>
        )}
        <h1 style={{ margin: 0 }}>{t('multiPlayback.title')}</h1>
        <Link to="/live/grid">{t('multiPlayback.liveGridLink')}</Link>
        {/* ls-loop MV.5：從這裡帶攝影機與播放頭去匯出 */}
        <Link to={`/vms/export?ids=${encodeURIComponent(selectedIds.join(','))}${playheadUs !== null ? `&t=${Math.round(playheadUs)}` : ''}`} data-testid="mc-export-link">
          {t('vmsExport.title')}
        </Link>
      </header>
      <p className="text-secondary" style={{ margin: 0 }}>{t('multiPlayback.subtitle', { count: MAX_CAMS })}</p>

      <div className="pb-toolbar">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label={t('playback.datePickerLabel')} className="pb-date" />
        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>{t('multiPlayback.pickLabel', { count: MAX_CAMS })}</span>
      </div>

      {/* ls-loop MV.3：存 / 開版面（含裝置分組）；回放離線的也能開（雲端錄影照看） */}
      <LayoutBar devices={devices} openable={cameras.map((d) => d.device_id)} max={MAX_CAMS} selected={selectedIds} onOpen={setSelectedIds} />

      {/* camera multi-select（沿用 LiveGrid picker 樣式） */}
      <div className="pb-mc-picker" data-testid="mc-picker">
        {cameras.map((d) => (
          <button
            key={d.device_id}
            onClick={() => toggle(d.device_id)}
            aria-pressed={selectedIds.includes(d.device_id)}
            data-testid={`mc-pick-${d.device_id}`}
            className="pick-chip"
          >
            {deviceName(d)}
          </button>
        ))}
        {cameras.length === 0 && <p className="text-secondary">{t('multiPlayback.noCameras')}</p>}
      </div>

      {toast && <p className="pb-toast" role="status">{toast}</p>}

      {gatedCams.length > 0 && (
        <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('multiPlayback.someUnavailable', { names: gatedCams.map((c) => deviceName(c.device)).join('、') })}
        </p>
      )}

      {selectedIds.length === 0 ? (
        <p className="text-secondary">{t('multiPlayback.pickToStart', { count: MAX_CAMS })}</p>
      ) : (
        <>
          {/* shared transport */}
          <div className="pb-mc-transport card" data-testid="mc-transport">
            <button className="btn-primary" onClick={togglePlay} disabled={!anyReady} data-testid="mc-playpause" aria-label={playing ? t('multiPlayback.pause') : t('multiPlayback.play')}>
              {playing ? t('multiPlayback.pause') : t('multiPlayback.play')}
            </button>
            <span className="pb-mc-clock" data-testid="mc-clock">{playheadUs !== null ? timeOnly(playheadUs, selZone) : '--:--:--'}</span>
            <label className="pb-speed-wrap">
              <span className="text-secondary">{t('playback.speed')}</span>
              <select className="pb-speed" aria-label={t('playback.speedLabel')} value={rate} onChange={(e) => setRate(Number(e.target.value))} data-testid="mc-speed">
                {PLAYBACK_SPEEDS.map((s) => (
                  <option key={s} value={s}>{s}×</option>
                ))}
              </select>
            </label>
          </div>

          {/* video grid */}
          <div className="pb-mc-grid" data-testid="mc-grid" style={{ gridTemplateColumns: readyCams.length <= 1 ? '1fr' : `repeat(${cols}, 1fr)` }}>
            {readyCams.map((c, i) => (
              <CamCell key={c.device.device_id} device={c.device} spans={perCamSpans[i] ?? []} playheadUs={playheadUs} playing={playing} rate={rate} />
            ))}
          </div>

          {!anyReady && anyLoading && <p className="text-secondary">{t('multiPlayback.loading')}</p>}

          {/* shared scrubber（union 覆蓋 + 共用 playhead），下方每台一條 per-gap 覆蓋帶 */}
          {anyReady && playheadUs !== null && (
            <>
              <TimelineStrip range={win} clips={unionRaw} events={[]} playheadUs={playheadUs} onSeek={onSeek} onSeekCommit={onSeek} />
              <div className="pb-mc-lanes" data-testid="mc-lanes">
                {readyCams.map((c, i) => (
                  <CoverageLane key={c.device.device_id} device={c.device} spans={perCamSpans[i] ?? []} range={win} />
                ))}
              </div>
            </>
          )}

          {anyReady && perCamSpans.every((sp) => sp.every((x) => x.kind !== 'recorded')) && readyCams.every((c) => c.load.localState !== 'pending') && (
            <p className="text-secondary">{t('multiPlayback.emptyDay')}</p>
          )}
        </>
      )}
    </section>
  );
}
